// frontend/src/components/ActionPlans/APLookupsSettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apLookupsAPI } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import SearchableSelect from '../Common/SearchableSelect';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiCheck } from 'react-icons/fi';

// ── Inline edit row ────────────────────────────────────────────────────────────
const EditRow = ({ fields, values, onChange, onSave, onCancel, saving }) => (
  <tr style={{ background: '#f0fdf9' }}>
    {fields.map(f => (
      <td key={f.key} style={{ padding: '6px 10px' }}>
        {f.type === 'select' ? (
          <SearchableSelect value={values[f.key] || ''} onChange={v => onChange(f.key, v)}
            options={f.options} getOptionLabel={o => o.label} getOptionValue={o => o.value}
            placeholder={f.placeholder || '—'}/>
        ) : f.type === 'toggle' ? (
          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
            <input type="checkbox" checked={!!values[f.key]}
              onChange={e => onChange(f.key, e.target.checked)}/>
            <span style={{ fontSize:12 }}>Ativo</span>
          </label>
        ) : (
          <input className="form-control" style={{ fontSize:13, height:32 }}
            value={values[f.key] || ''} onChange={e => onChange(f.key, e.target.value)}
            placeholder={f.placeholder}/>
        )}
      </td>
    ))}
    <td style={{ padding:'6px 10px', whiteSpace:'nowrap' }}>
      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}
        style={{ marginRight:4 }}>
        <FiCheck size={12}/> {saving ? '…' : 'Guardar'}
      </button>
      <button className="btn btn-secondary btn-sm" onClick={onCancel}>
        <FiX size={12}/>
      </button>
    </td>
  </tr>
);

// ── Generic CRUD table ─────────────────────────────────────────────────────────
const CrudTable = ({ title, cols, rows, loading, onSave, onDelete, addLabel = 'Adicionar' }) => {
  const [editId,   setEditId]   = useState(null);   // null=none, 'new'=new row
  const [formData, setFormData] = useState({});
  const [saving,   setSaving]   = useState(false);

  const openNew = () => {
    const defaults = Object.fromEntries(cols.map(c => [c.key, c.default ?? '']));
    defaults.is_active = true;
    setFormData(defaults);
    setEditId('new');
  };
  const openEdit = (row) => {
    setFormData({ ...row });
    setEditId(row[cols[0].idKey] ?? row.id);
  };
  const close = () => { setEditId(null); setFormData({}); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editId === 'new' ? null : editId, formData);
      close();
    } finally { setSaving(false); }
  };

  const allCols = [...cols.filter(c => c.key !== 'is_active'), { key:'is_active', label:'Ativo', type:'toggle' }];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <span className="text-muted" style={{ fontSize:13 }}>{rows.length} {title.toLowerCase()}</span>
        <button className="btn btn-primary btn-sm" onClick={openNew}>
          <FiPlus size={13}/> {addLabel}
        </button>
      </div>

      {loading ? (
        <div style={{ padding:24, textAlign:'center', color:'var(--gray-400)' }}>A carregar…</div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'var(--gray-50)', borderBottom:'2px solid var(--gray-200)' }}>
                {cols.map(c => (
                  <th key={c.key} style={{ padding:'8px 10px', textAlign:'left',
                    fontWeight:600, color:'var(--gray-600)', fontSize:12 }}>
                    {c.label}
                  </th>
                ))}
                <th style={{ padding:'8px 10px', width:90, fontWeight:600,
                  color:'var(--gray-600)', fontSize:12 }}>Ativo</th>
                <th style={{ width:90 }}/>
              </tr>
            </thead>
            <tbody>
              {/* New row at top */}
              {editId === 'new' && (
                <EditRow fields={allCols} values={formData}
                  onChange={(k,v) => setFormData(f => ({...f,[k]:v}))}
                  onSave={handleSave} onCancel={close} saving={saving}/>
              )}
              {rows.length === 0 && editId !== 'new' && (
                <tr><td colSpan={cols.length+2} style={{ textAlign:'center',
                  padding:32, color:'var(--gray-400)' }}>
                  Sem registos. Clique em "{addLabel}" para adicionar.
                </td></tr>
              )}
              {rows.map((row, i) => {
                const id = row[cols[0]?.idKey] ?? row.area_id ?? row.sub_area_id
                        ?? row.category_id ?? i;
                const isEditing = editId === id;
                if (isEditing) return (
                  <EditRow key={id} fields={allCols} values={formData}
                    onChange={(k,v) => setFormData(f => ({...f,[k]:v}))}
                    onSave={handleSave} onCancel={close} saving={saving}/>
                );
                return (
                  <tr key={id} style={{
                    borderBottom:'1px solid var(--gray-100)',
                    background: !row.is_active ? '#fafafa' : 'white',
                    opacity: !row.is_active ? 0.65 : 1,
                  }}>
                    {cols.map(c => (
                      <td key={c.key} style={{ padding:'7px 10px' }}>
                        {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                      </td>
                    ))}
                    <td style={{ padding:'7px 10px' }}>
                      <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10,
                        background: row.is_active ? '#d1fae5' : '#f3f4f6',
                        color:      row.is_active ? '#059669' : '#9ca3af' }}>
                        {row.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td style={{ padding:'7px 10px', whiteSpace:'nowrap' }}>
                      <button className="btn-action btn-edit" title="Editar"
                        onClick={() => openEdit(row)} style={{ marginRight:4 }}>
                        <FiEdit2 size={13}/>
                      </button>
                      <button className="btn-action btn-delete" title="Eliminar"
                        onClick={() => onDelete(id, row)}
                        disabled={row.plan_count > 0 || row.sub_area_count > 0}>
                        <FiTrash2 size={13}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const APLookupsSettings = () => {
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [tab,        setTab]        = useState('areas');
  const [areas,      setAreas]      = useState([]);
  const [subAreas,   setSubAreas]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingA,   setLoadingA]   = useState(true);
  const [loadingS,   setLoadingS]   = useState(true);
  const [loadingC,   setLoadingC]   = useState(true);

  const loadAreas = useCallback(async () => {
    setLoadingA(true);
    try { const r = await apLookupsAPI.getAreas(); if (r.data.success) setAreas(r.data.data); }
    catch { toast_error('Erro ao carregar áreas'); }
    finally { setLoadingA(false); }
  }, []);

  const loadSubAreas = useCallback(async () => {
    setLoadingS(true);
    try { const r = await apLookupsAPI.getSubAreas(); if (r.data.success) setSubAreas(r.data.data); }
    catch { toast_error('Erro ao carregar sub-áreas'); }
    finally { setLoadingS(false); }
  }, []);

  const loadCategories = useCallback(async () => {
    setLoadingC(true);
    try { const r = await apLookupsAPI.getCategories(); if (r.data.success) setCategories(r.data.data); }
    catch { toast_error('Erro ao carregar categorias'); }
    finally { setLoadingC(false); }
  }, []);

  useEffect(() => { loadAreas(); loadSubAreas(); loadCategories(); }, []);

  // ── Areas CRUD ──────────────────────────────────────────────────────────────
  const saveArea = async (id, data) => {
    try {
      if (id) { await apLookupsAPI.updateArea(id, data); toast_success('Área atualizada'); }
      else    { await apLookupsAPI.createArea(data);     toast_success('Área criada'); }
      await loadAreas(); await loadSubAreas(); // sub-area list shows area names
    } catch (e) { toast_error(e.response?.data?.error || 'Erro ao guardar'); throw e; }
  };
  const deleteArea = async (id, row) => {
    if (row.sub_area_count > 0) { toast_error(`Tem ${row.sub_area_count} sub-área(s). Elimine-as primeiro.`); return; }
    if (row.plan_count > 0)     { toast_error(`Em uso em ${row.plan_count} plano(s).`); return; }
    if (!window.confirm(`Eliminar área "${row.area_name}"?`)) return;
    try { await apLookupsAPI.deleteArea(id); toast_success('Eliminado'); await loadAreas(); }
    catch (e) { toast_error(e.response?.data?.error || 'Erro'); }
  };

  // ── Sub-Areas CRUD ──────────────────────────────────────────────────────────
  const saveSubArea = async (id, data) => {
    try {
      if (id) { await apLookupsAPI.updateSubArea(id, data); toast_success('Sub-área atualizada'); }
      else    { await apLookupsAPI.createSubArea(data);     toast_success('Sub-área criada'); }
      await loadSubAreas();
    } catch (e) { toast_error(e.response?.data?.error || 'Erro ao guardar'); throw e; }
  };
  const deleteSubArea = async (id, row) => {
    if (row.plan_count > 0) { toast_error(`Em uso em ${row.plan_count} plano(s).`); return; }
    if (!window.confirm(`Eliminar sub-área "${row.sub_area_name}"?`)) return;
    try { await apLookupsAPI.deleteSubArea(id); toast_success('Eliminado'); await loadSubAreas(); }
    catch (e) { toast_error(e.response?.data?.error || 'Erro'); }
  };

  // ── Categories CRUD ─────────────────────────────────────────────────────────
  const saveCategory = async (id, data) => {
    try {
      if (id) { await apLookupsAPI.updateCategory(id, data); toast_success('Categoria atualizada'); }
      else    { await apLookupsAPI.createCategory(data);     toast_success('Categoria criada'); }
      await loadCategories();
    } catch (e) { toast_error(e.response?.data?.error || 'Erro ao guardar'); throw e; }
  };
  const deleteCategory = async (id, row) => {
    if (row.plan_count > 0) { toast_error(`Em uso em ${row.plan_count} plano(s).`); return; }
    if (!window.confirm(`Eliminar categoria "${row.category_name}"?`)) return;
    try { await apLookupsAPI.deleteCategory(id); toast_success('Eliminado'); await loadCategories(); }
    catch (e) { toast_error(e.response?.data?.error || 'Erro'); }
  };

  // ── Column definitions ──────────────────────────────────────────────────────
  const areaCols = [
    { key:'area_code',  label:'Código',     idKey:'area_id', placeholder:'EX: PRODUCAO' },
    { key:'area_name',  label:'Nome',        placeholder:'Nome da área' },
    { key:'sub_area_count', label:'Sub-áreas', render: v => v ?? 0 },
    { key:'plan_count', label:'Planos',      render: v => v ?? 0 },
  ];

  const areaOptions = areas.map(a => ({
    value: a.area_id, label: `${a.area_code} — ${a.area_name}`,
  }));

  const subAreaCols = [
    { key:'area_id',       label:'Área',       idKey:'sub_area_id',
      type:'select', options: areaOptions, placeholder:'— selecionar área —',
      render: (v, row) => row.area_name ? `${row.area_code} — ${row.area_name}` : '—' },
    { key:'sub_area_code', label:'Código',      placeholder:'EX: CORTE' },
    { key:'sub_area_name', label:'Nome',        placeholder:'Nome da sub-área' },
    { key:'plan_count',    label:'Planos',      render: v => v ?? 0 },
  ];

  const categoryCols = [
    { key:'category_code', label:'Código', idKey:'category_id', placeholder:'EX: QUALIDADE' },
    { key:'category_name', label:'Nome',   placeholder:'Nome da categoria' },
    { key:'plan_count',    label:'Planos', render: v => v ?? 0 },
  ];

  const TABS = [
    { key:'areas',      label:`Áreas (${areas.length})` },
    { key:'sub_areas',  label:`Sub-Áreas (${subAreas.length})` },
    { key:'categories', label:`Categorias (${categories.length})` },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>⚙️ Configurações — Planos de Ação</h2>
          <p className="text-muted" style={{ fontSize:13, marginTop:2 }}>
            Gerir áreas, sub-áreas e categorias
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="ap-tabs" style={{ marginBottom:0 }}>
        {TABS.map(t => (
          <button key={t.key} type="button"
            className={`ap-tab ${tab===t.key?'active':''}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="form-section">
        {tab === 'areas' && (
          <CrudTable title="Áreas" cols={areaCols} rows={areas}
            loading={loadingA} onSave={saveArea} onDelete={deleteArea}
            addLabel="Nova Área"/>
        )}
        {tab === 'sub_areas' && (
          <CrudTable title="Sub-Áreas" cols={subAreaCols} rows={subAreas}
            loading={loadingS} onSave={saveSubArea} onDelete={deleteSubArea}
            addLabel="Nova Sub-Área"/>
        )}
        {tab === 'categories' && (
          <CrudTable title="Categorias" cols={categoryCols} rows={categories}
            loading={loadingC} onSave={saveCategory} onDelete={deleteCategory}
            addLabel="Nova Categoria"/>
        )}
      </div>

      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            requireConfirm={toast.requireConfirm}
            onConfirm={toast.onConfirm}
            onClose={removeToast}
          />
        ))}
      </div>



    </div>
  );
};

export default APLookupsSettings;